import { useEffect, useState } from "react";

export interface InputTextProps<T> {
  feedbackValid?: string;
  feedbackInvalid?: string;
  validator?: (val: T) => {
    valid?: boolean;
    feedback?: string;
    noValidate?: boolean;
  } | void;
  validated?: boolean;
  onFocus?: () => void;
}

export interface FormInputState<T> {
  readonly value: T;
  readonly valid: boolean;
  readonly invalid: boolean;
  readonly feedbackValid: string;
  readonly feedbackInvalid: string;
  readonly disabled?: boolean;
}

export interface FormInputController<T> {
  readonly setValue: React.Dispatch<React.SetStateAction<T>>;
  readonly peek?: () => FormInputState<T>;
  readonly onFocus: () => void;
  readonly setDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setValid?: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setInvalid?: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setFeedbackValid?: React.Dispatch<React.SetStateAction<string>>;
  readonly setFeedbackInvalid?: React.Dispatch<React.SetStateAction<string>>;
}

const useFormInput = <T>(defaultValue: T, p?: InputTextProps<T>) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [value, setValue] = useState<T>(defaultValue);
  const [valid, setValid] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [feedbackValid, setFeedbackValid] = useState(p?.feedbackValid ?? "");
  const [feedbackInvalid, setFeedbackInvalid] = useState(
    p?.feedbackInvalid ?? "",
  );
  const validated = p?.validated ?? false;

  useEffect(() => {
    if (p?.validator) {
      const res = p.validator(value);
      if (res) {
        if (res.noValidate !== undefined) {
          if (res.noValidate) {
            setValid(false);
            setInvalid(false);
            return;
          }
        }
        if (res?.valid) {
          setValid(true);
          setInvalid(false);
          if (res.feedback) {
            setFeedbackValid(res.feedback);
          }
        } else {
          setValid(false);
          setInvalid(true);
          if (res?.feedback) {
            setFeedbackInvalid(res.feedback);
          }
        }
      }
    }
  }, [p, value, validated]);

  const peek = () => {
    return {
      value,
      valid: valid,
      invalid: invalid,
      feedbackValid: feedbackValid,
      feedbackInvalid: feedbackInvalid,
    };
  };

  const onFocus = () => {
    if (p?.onFocus) {
      p.onFocus();
    }
  };

  return [
    {
      value,
      disabled,
      valid: validated ? valid : false,
      invalid: validated ? invalid : false,
      feedbackValid: validated && valid ? feedbackValid : "",
      feedbackInvalid: validated && invalid ? feedbackInvalid : "",
    },
    {
      setValue,
      setDisabled,
      peek,
      onFocus,
      setValid,
      setInvalid,
      setFeedbackValid,
      setFeedbackInvalid,
    },
  ] as const;
};

export default useFormInput;
